import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import {
    deviationTypesTemplate,
    addDeviationTypeTemplate,
    updateDeviationTypeTemplate,
    deleteDeviationTypeTemplate,
} from 'apollo/templates/deviation';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Box, Button, IconButton, List, ListItem, ListItemButton, ListItemText, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AddUpdateDeviationTypeDialog from './add-update-deviation-type-dialog/add-update-deviation-type-dialog.component';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import { DeviationType } from 'models/deviation';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

const DeviationSettings: FC = () => {
    const { t: translate } = useTranslation(['deviations']);
    const [addDialogOpen, setDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deviationTypes, setDeviationTypes] = useState<DeviationType[]>([] as DeviationType[]);
    const [snackbarOpen, setSnackbarOpen] = useState({
        addedDeviationType: false,
        updatedDeviationType: false,
        deletedDeviationType: false,
    });
    const [deviationType, setDeviationType] = useState<DeviationType>({} as DeviationType);
    const [deviationTypeId, setDeviationTypeId] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(deviationTypesTemplate, {
        variables: {},
        onCompleted: ({ deviationTypes }) => {
            setDeviationTypes(deviationTypes);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [addDeviationType] = useMutation(addDeviationTypeTemplate, {
        onCompleted: ({ addDeviationType }) => {
            setDeviationTypes(addDeviationType);
            setSnackbarOpen({ ...snackbarOpen, addedDeviationType: true });
            setDialogOpen(false);
            setDeviationType({} as DeviationType);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [updateDeviationType] = useMutation(updateDeviationTypeTemplate, {
        onError: (err) => {
            console.log(err, 'error');
        },
        onCompleted: ({ updateDeviationType }) => {
            setDeviationTypes(updateDeviationType);
            setSnackbarOpen({ ...snackbarOpen, updatedDeviationType: true });
            setDialogOpen(false);
            setDeviationType({} as DeviationType);
        },
    });

    const [deleteDeviationType] = useMutation(deleteDeviationTypeTemplate, {
        onCompleted: ({ deleteDeviationType }) => {
            setDeviationTypes(deleteDeviationType);
            setSnackbarOpen({ ...snackbarOpen, deletedDeviationType: true });
            setConfirmDialogOpen(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const reactivateSnackbar = () => {
        setSnackbarOpen({
            addedDeviationType: false,
            updatedDeviationType: false,
            deletedDeviationType: false,
        });
    };

    const onDialogClose = () => {
        setDialogOpen(false);
        setConfirmDialogOpen(false);
        setDeviationType({} as DeviationType);
    };

    const openDialog = () => {
        reactivateSnackbar();
        setDialogOpen(true);
    };

    const openConfirmDialog = (deviationTypeId: string) => {
        reactivateSnackbar();
        setDeviationTypeId(deviationTypeId);
        setConfirmDialogOpen(true);
    };

    const handleOk = async () => {
        onDeviationDelete(deviationTypeId);
        setConfirmDialogOpen(false);
    };

    const openUpdateModal = (deviationType: DeviationType) => {
        reactivateSnackbar();
        setDeviationType(deviationType);
        setDialogOpen(true);
    };

    const onDeviationAdd = async (deviationType: DeviationType) => {
        await addDeviationType({
            variables: {
                code: deviationType.code,
                description: deviationType.description,
                type: deviationType.type,
            },
        });
    };

    const onDeviationUpdate = async (deviationType: DeviationType) => {
        await updateDeviationType({
            variables: {
                deviationTypeId: deviationType._id,
                code: deviationType.code,
                description: deviationType.description,
                type: deviationType.type,
            },
        });
    };
    const onDeviationDelete = async (deviationTypeId: string) => {
        await deleteDeviationType({
            variables: { deviationTypeId: deviationTypeId },
        });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h2" title={translate('DEVIATIONS')} />
            <Box mb={2}>{translate('DEVIATIONS_DESC')}</Box>
            <Box mb={2}>
                <Button onClick={openDialog}>{translate('ADD_DEVIATION_TYPE')}</Button>
            </Box>
            {deviationTypes ? (
                <Paper variant="outlined">
                    <List dense>
                        {deviationTypes.map((deviation) => (
                            <ListItem
                                key={deviation._id}
                                secondaryAction={
                                    <IconButton onClick={() => openConfirmDialog(deviation._id as string)} edge="end">
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemButton onClick={() => openUpdateModal(deviation)}>
                                    <ListItemText primary={`${deviation.code} - ${deviation.description}`} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ) : undefined}
            <AddUpdateDeviationTypeDialog
                deviation={deviationType}
                open={addDialogOpen}
                onDialogClose={onDialogClose}
                onDeviationAdd={onDeviationAdd}
                onDeviationUpdate={onDeviationUpdate}
            />
            <ConfirmationDialog
                open={confirmDialogOpen}
                handleClose={onDialogClose}
                handleOk={handleOk}
                title={translate('deviations:CONFIRM_TITLE')}
                confirm={translate('deviations:CONFIRM_DELETE')}
                process={translate('common:DELETE')}
            />
            {snackbarOpen.addedDeviationType ? (
                <AlertSnackbar type="success" message={translate('deviations:DEVIATION_TYPE_ADDED')} />
            ) : undefined}
            {snackbarOpen.updatedDeviationType ? (
                <AlertSnackbar type="success" message={translate('deviations:DEVIATION_TYPE_UPDATED')} />
            ) : undefined}
            {snackbarOpen.deletedDeviationType ? (
                <AlertSnackbar type="success" message={translate('deviations:DEVIATION_TYPE_DELETED')} />
            ) : undefined}
        </Fragment>
    );
};

export default DeviationSettings;
